import {
  getPolicyExpireDate,
  isSilentMode,
  getSilentModeDate,
  getMaxKeysPerPolicy
} from 'app/stores/alerting/policyUtils';
import {
  APPLICATION_TO_BUMP_EPOCH_SETTINGS,
  INTERFACE_FILTER_MAP,
  POLICY_AGG_FUNCTIONS,
  POLICY_AGG_FUNCTIONS_DESERIALIZE,
  POLICY_APPLICATIONS,
  ALERT_MANAGER_ACTIVATION_MODES,
  POLICY_DEFAULT_VALUES
} from '@kentik/ui-shared/alerting/constants';
import { getTopLevelApplication, checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';
import cidrMetrics from '@kentik/ui-shared/dictionary/cidrMetrics';
import $metrics from 'app/stores/metrics/$metrics';
import safelyParseJSON from 'core/util/safelyParseJson';
import { convertInterfacesToFilterGroup, getKmetricMeasurementFilterGroup } from 'app/util/filters';
import { getMeasurementModelDataFromPolicy, mapKmetricDimensions } from 'app/util/policies';
import { GREEK_METRICS, GREEK_FACTORS } from 'app/util/constants';

// Removes filters we don't want to show in the UI, and which will be rebuilt on serialize.
export function removeHiddenKmetricsFilters(policy, measurementModel) {
  const measurementName = measurementModel.get('measurement');
  const storage = measurementModel.get('storage') || {};
  const metadata = policy.applicationMetadata || {};
  const metadataInterfaces = metadata.interfaces || {};
  const interfaceKeys = Object.keys(metadataInterfaces);
  const policyFilterGroups = policy.filters?.filterGroups || [];
  const kmetricsFiltersToRemove = [];
  const interfaceFiltersToRemove = [];
  const kmPrefix = $metrics.getAppProtocolDimension(measurementName, null, measurementModel);

  // Always remove these
  kmetricsFiltersToRemove.push(`${kmPrefix}km_measurement_name`);
  kmetricsFiltersToRemove.push('km_device_id');

  // Handle separately to reduce iterations, and for stricter comparisons
  if (interfaceKeys.length > 0) {
    interfaceKeys.forEach((key) => {
      const interfaceValue = metadataInterfaces[key];
      let { dimension } = INTERFACE_FILTER_MAP[key] || {};

      // km_device_id is a special case; don't prefix it for filters
      if (dimension && dimension !== 'km_device_id') {
        const dimStorage = storage.Dimensions?.[dimension] || {};
        const dimColumn = dimStorage.Column || dimStorage.QueryColumn;
        dimension = `${kmPrefix}${dimColumn}`;
      }

      (Array.isArray(interfaceValue) ? interfaceValue : [interfaceValue]).forEach((intfVal) => {
        interfaceFiltersToRemove.push({
          filterField: dimension,
          filterValue: intfVal?.toString()
        });
      });
    });
  }

  // Now let's remove any matches
  return (
    policyFilterGroups
      .map((group) => {
        // kmetrics filters will only ever be a length of 1, so only check when that's the case
        if (group.filters.length === 1) {
          group.filters = kmetricsFiltersToRemove.some((filterField) => filterField === group.filters[0].filterField)
            ? []
            : [group.filters[0]];
        }

        if (interfaceFiltersToRemove.length > 0) {
          // Only remove group if every filter in it has a field+value match
          group.filters = group.filters.every((policyFilter) => {
            const { filterField, filterValue } = policyFilter;
            return interfaceFiltersToRemove.some((f) => f.filterField === filterField && f.filterValue === filterValue);
          })
            ? []
            : group.filters;
        }

        return group;
      })
      // Clean up empty filter groups
      .filter((group) => group.filters.length > 0)
  );
}

function transformToMinutes(duration) {
  const match = duration?.match(/^(\d+)(h|m|s)$/);
  const timeWindow = parseInt(match?.[1]) || 0;
  const shortTimeUnit = match?.[2] || 'm';

  // it's seconds - convert to minutes
  if (shortTimeUnit === 's') {
    return timeWindow / 60;
  }

  // it's already minutes
  if (shortTimeUnit === 'm') {
    return timeWindow;
  }

  // it's hours - convert to minutes
  if (shortTimeUnit === 'h') {
    return timeWindow * 60;
  }

  return duration;
}

function deserialize(policy) {
  const {
    id,
    customerID,
    companyID,
    userID,
    silentModeExpireDate,
    evaluationPeriod,
    dimensionGroupingOptions,
    applicationMetadata,
    uiProperties = {},
    ...restPolicy
  } = policy;
  // Using fallbacks for both of hte following because if either is undefined, the subsequent Math.min evaluates to NaN
  const maxKeysPerPolicy = getMaxKeysPerPolicy(restPolicy.application) || 0;
  const policyNTopKeysEvaluated = restPolicy.nTopKeysEvaluated || POLICY_DEFAULT_VALUES.nTopKeysEvaluated;

  // To prevent unsaveable policies, cap nTopKeysEvaluated with company permissions
  // See: https://github.com/kentik/ui-app/issues/25777
  restPolicy.nTopKeysEvaluated = `${Math.min(maxKeysPerPolicy, policyNTopKeysEvaluated)}`;

  // we no longer have cloud
  if (restPolicy.application === POLICY_APPLICATIONS.CLOUD) {
    restPolicy.application = POLICY_APPLICATIONS.CORE;
  }

  // if its a toggle-mode policy, get the activate and clearance delays from the rule
  if (restPolicy?.activationSettings?.mode === ALERT_MANAGER_ACTIVATION_MODES.TOGGLE) {
    // delays are stored at the rule severity level but all levels must have the same delays
    // set the delays to whatever is on the 1st severity or set it to undefined
    restPolicy.activationSettings.activationDelay = transformToMinutes(
      restPolicy.rule?.levels?.[0]?.toggle?.activation_delay
    );
    restPolicy.activationSettings.clearanceDelay = transformToMinutes(
      restPolicy.rule?.levels?.[0]?.toggle?.clearance_delay
    );
  }

  if (dimensionGroupingOptions) {
    dimensionGroupingOptions.GroupingDimensions = dimensionGroupingOptions.GroupingDimensions || [];
  }

  const dimension_grouping_enabled =
    dimensionGroupingOptions?.GroupingDimensions.length >= 1 && dimensionGroupingOptions.MaxPerGroup > 0;

  // The UI derives the enabled state from the selected grouping dimensions, so clear them when grouping is inactive
  if (dimensionGroupingOptions && !dimension_grouping_enabled) {
    dimensionGroupingOptions.GroupingDimensions = [];
  }

  restPolicy.dimensions = restPolicy.dimensions.map((dimension) => {
    if (!dimension && !dimension.includes('_cidr_')) {
      return dimension;
    }

    const [stem, cidrs] = dimension.split('_cidr_');

    if (stem && cidrs && cidrMetrics.includes(stem)) {
      const [cidr, cidr6] = cidrs.split('_');
      uiProperties.cidr = cidr;
      uiProperties.cidr6 = cidr6;
      return stem;
    }

    return dimension;
  });

  if (dimensionGroupingOptions) {
    dimensionGroupingOptions.GroupingDimensions = dimensionGroupingOptions.GroupingDimensions.map((dimension) => {
      const [stem, cidrs] = (dimension || '').split('_cidr_');
      return stem && cidrs && cidrMetrics.includes(stem) ? stem : dimension;
    });
  }

  // clear out default dashboard id
  if (restPolicy.dashboardID === '49') {
    delete restPolicy.dashboardID;
  }

  // Need applicationMetadata available before getting measurement model
  restPolicy.applicationMetadata = applicationMetadata !== undefined ? safelyParseJSON(applicationMetadata) : {};

  // if it's a metric policy, transform filters into properties on this policy
  if (checkApplication.isMetric(restPolicy.application)) {
    const { measurement } = restPolicy.applicationMetadata;
    const measurementModel = $metrics.measurementModel(measurement);
    uiProperties.selectedMeasurement = measurement;
    uiProperties.aggregateFunction =
      POLICY_AGG_FUNCTIONS_DESERIALIZE[
        restPolicy.customMetricDefinitions?.[restPolicy.metrics?.[0]]?.aggregateFunction
      ] || 'avg';

    // Unwrap the nested user filter group added during serialize
    const rootFilterGroups = restPolicy.filters?.filterGroups || [];
    const wrapperGroups = rootFilterGroups.filter(
      (group) =>
        !group.filters?.length &&
        !group.savedFilters?.length &&
        !group.saved_filters?.length &&
        group.filterGroups?.length > 0
    );

    if (wrapperGroups.length === 1) {
      const [wrapper] = wrapperGroups;
      restPolicy.filters = {
        ...restPolicy.filters,
        connector: wrapper.connector,
        filterGroups: wrapper.filterGroups.concat(rootFilterGroups.filter((group) => group !== wrapper))
      };
    }

    if (measurementModel) {
      restPolicy.filters.filterGroups = removeHiddenKmetricsFilters(restPolicy, measurementModel);
      restPolicy.dimensions = mapKmetricDimensions(restPolicy);

      if (dimensionGroupingOptions) {
        dimensionGroupingOptions.GroupingDimensions = mapKmetricDimensions({
          ...restPolicy,
          dimensions: dimensionGroupingOptions.GroupingDimensions
        });
      }
    }

    // The Filters that we want to show to user, which are quite different than the filters stored with the Policy,
    // are stored in `applicationMetadata.filters`
    if (restPolicy.applicationMetadata?.filters?.filterGroups.length > 0) {
      restPolicy.filters = restPolicy.applicationMetadata.filters;
    }

    restPolicy.filters = $metrics.dimensionFilterKdeToReconTransformer(restPolicy.filters, measurement);
  }

  const silenced = isSilentMode(silentModeExpireDate);
  const calculatedSilentModeExpireDate = getSilentModeDate(silentModeExpireDate);

  // Only multiply by comparisonValueFactor for greek metrics
  if (GREEK_METRICS.includes(restPolicy.metrics?.[0])) {
    let minTrafficValueFactor = GREEK_FACTORS[0].value;

    // iterate through GREEK_FACTORS and find the factor that brings our value closest to 1000 or below
    for (const factor of GREEK_FACTORS) {
      if (restPolicy.minTrafficValue / factor.value < 1000) {
        minTrafficValueFactor = factor.value;
        break;
      }
    }

    const minTrafficNumberValue = parseInt(restPolicy.minTrafficValue);

    if (restPolicy.minTrafficValue && !Number.isNaN(minTrafficNumberValue) && minTrafficNumberValue > 0) {
      restPolicy.minTrafficValueFactor = minTrafficValueFactor;
      restPolicy.minTrafficValue = minTrafficNumberValue / minTrafficValueFactor;
    }
  }

  const policyId = parseInt(id, 10);

  return {
    ...restPolicy,
    labelIds: (restPolicy.labelIds || []).map(Number),
    application: restPolicy.application || POLICY_APPLICATIONS.CORE,
    evaluationPeriod: parseInt(evaluationPeriod.replace('s', ''), 10),
    silenced,
    silentModeExpireDate: calculatedSilentModeExpireDate,
    id: Number.isNaN(policyId) ? undefined : policyId,
    user_id: userID,
    dimensionGroupingOptions,
    uiProperties: {
      ...uiProperties,
      dimension_grouping_enabled
    }
  };
}

function serialize(policy) {
  const {
    rule,
    customer_id,
    user_id,
    minTrafficValue,
    minTrafficValueFactor,
    silenced,
    silentModeExpireDate,
    evaluationPeriod,
    applicationMetadata = {},
    uiProperties = {},
    ...restPolicy
  } = policy;

  let { dimensionGroupingOptions, cidr, cidr6 } = policy;
  const dimensionGroupingEnabled = uiProperties.dimension_grouping_enabled;

  if (uiProperties.cidr) {
    cidr = uiProperties.cidr;
  }

  if (uiProperties.cidr6) {
    cidr6 = uiProperties.cidr6;
  }

  // protect against weird cidr values but mostly undefined
  cidr = parseInt(cidr);
  if (Number.isNaN(cidr) || cidr < 0 || cidr > 32) {
    cidr = 32;
  }
  cidr6 = parseInt(cidr6);
  if (Number.isNaN(cidr6) || cidr6 < 0 || cidr6 > 128) {
    cidr6 = 128;
  }

  const cidrSuffix = `_cidr_${cidr}_${cidr6}`;
  const defaultCidrSuffix = '_cidr_32_128';

  const withCidrSuffix = (dimension) =>
    cidrMetrics.includes(dimension) && cidrSuffix !== defaultCidrSuffix ? dimension + cidrSuffix : dimension;

  if (dimensionGroupingOptions) {
    const selected = dimensionGroupingOptions.GroupingDimensions || [];
    dimensionGroupingOptions = {
      ...dimensionGroupingOptions,
      // Must match the saved policy dimension names exactly, including any CIDR suffix
      GroupingDimensions: (policy.dimensions || [])
        .filter((dimension) => selected.includes(dimension))
        .map(withCidrSuffix)
    };
  }

  if (!dimensionGroupingEnabled || !dimensionGroupingOptions?.GroupingDimensions.length) {
    dimensionGroupingOptions = null;
  }

  let minTrafficValueToUse = minTrafficValue || 0;
  const minTrafficNumberValue = parseInt(minTrafficValue);

  if (minTrafficValue && !Number.isNaN(minTrafficNumberValue) && minTrafficNumberValue > 0 && minTrafficValueFactor) {
    minTrafficValueToUse = minTrafficNumberValue * minTrafficValueFactor;
  }

  const serializedPolicy = {
    ...restPolicy,
    minTrafficValue: `${minTrafficValueToUse}`,
    evaluationPeriod:
      restPolicy?.activationSettings?.mode === ALERT_MANAGER_ACTIVATION_MODES.TOGGLE ? '0s' : `${evaluationPeriod}s`,
    dashboardID: restPolicy.dashboardID || '49',
    dimensionGroupingOptions
  };

  if (Number.isNaN(policy.id)) {
    policy.id = undefined;
  }

  // Handle any custom dimensions
  serializedPolicy.dimensions = serializedPolicy.dimensions.map(withCidrSuffix);

  // ... just in case we missed a policy somehow during migrations, absolute safety measure

  serializedPolicy.application = serializedPolicy.application || POLICY_APPLICATIONS.CORE;

  // we no longer have cloud
  if (serializedPolicy.application === POLICY_APPLICATIONS.CLOUD) {
    serializedPolicy.application = POLICY_APPLICATIONS.CORE;
  }

  // If creating a fresh query-to-policy, convert the daysToExpire field into an expireDate
  if (Number.isInteger(uiProperties.daysToExpire)) {
    if (uiProperties.daysToExpire === -1) {
      // -1 means never expire, so clear expire date
      serializedPolicy.expireDate = null;
    } else if (uiProperties.daysToExpire > 0) {
      // Positive Convert days to expire date
      serializedPolicy.expireDate = getPolicyExpireDate(uiProperties.daysToExpire);
    }
    // Otherwise, leave expireDate unchanged
  }

  // Coerce falsey expire dates to null for API
  if (!serializedPolicy.expireDate) {
    serializedPolicy.expireDate = null;
  }

  // If not silenced, backdate it to simulate expiration. Otherwise, API defaults to +7 days.
  serializedPolicy.silentModeExpireDate = silenced ? silentModeExpireDate : '2020-01-01T00:00:01Z';

  // If it's a metric policy, we need to transform some policy measurement & properties into filters
  if (checkApplication.isMetric(serializedPolicy.application)) {
    const { metrics, dimensions } = serializedPolicy;
    const { measurement, fullMeasurement } = getMeasurementModelDataFromPolicy(policy);
    const measurementName = fullMeasurement?.measurement;
    if (String(measurementName).length && measurement !== applicationMetadata?.measurement) {
      applicationMetadata.measurement = measurementName;
    }

    const filterGroupsToSerialize = [];
    const customMetricDefinitions = {};

    metrics.forEach((metric) => {
      const matchingMetric = fullMeasurement.storage.Metrics[metric];

      if (matchingMetric) {
        customMetricDefinitions[metric] = {
          attribute: $metrics.getAppProtocolDimension(measurementName, matchingMetric.Column),
          // Users don't customize this value for threshold policies.
          // This could change in the future if requested.
          // For now we're hardcoding it to average.
          aggregateFunction: POLICY_AGG_FUNCTIONS[uiProperties?.aggregateFunction || 'avg']
        };
      }
    });

    if (applicationMetadata?.interfaces) {
      const interfaceFilters = convertInterfacesToFilterGroup(applicationMetadata.interfaces, fullMeasurement);

      if (interfaceFilters.length > 0) {
        filterGroupsToSerialize.push(...interfaceFilters);
      }
    }

    // add dimensions, metrics with custom mappings
    const toStorageDimension = (dimension) => {
      const dimensionStorage = fullMeasurement.storage.Dimensions?.[dimension] || {};
      const dimensionCol = dimensionStorage.Column || dimensionStorage.QueryColumn;
      return dimensionCol && $metrics.getAppProtocolDimension(measurementName, dimensionCol);
    };

    serializedPolicy.dimensions = dimensions
      .map(toStorageDimension)
      .concat([
        $metrics.getAppProtocolDimension(measurementName, 'km_measurement_name'),
        $metrics.getAppProtocolDimension(measurementName, 'km_device_id')
      ])
      .filter(Boolean);

    if (serializedPolicy.dimensionGroupingOptions) {
      const groupingDimensions = serializedPolicy.dimensionGroupingOptions.GroupingDimensions;
      const storageGroupingDimensions = groupingDimensions.map(toStorageDimension).filter(Boolean);
      serializedPolicy.dimensionGroupingOptions = storageGroupingDimensions.length
        ? { ...serializedPolicy.dimensionGroupingOptions, GroupingDimensions: storageGroupingDimensions }
        : null;
    }
    serializedPolicy.customMetricDefinitions = customMetricDefinitions;

    // Nest user filters so root Any doesn't short-circuit against the measurement group
    const userFilterGroups = restPolicy.filters.filterGroups || [];
    const wrappedUserFilterGroups = userFilterGroups.length
      ? [
          {
            connector: restPolicy.filters.connector,
            not: false,
            filters: [],
            filterGroups: userFilterGroups,
            saved_filters: []
          }
        ]
      : [];

    // takes all of the assembled Filters and transforms them into the format the API expects
    serializedPolicy.filters = {
      connector: 'All',
      filterGroups: $metrics.dimensionFilterReconToKdeTransformer(
        filterGroupsToSerialize.concat(wrappedUserFilterGroups),
        measurement
      )
    };

    // add the measurement as a filter group
    const measurementAsFilterGroup = getKmetricMeasurementFilterGroup(measurement);
    serializedPolicy.filters.filterGroups.push(measurementAsFilterGroup);
  }

  // have to stringify applicationMetadata
  if (Object.keys(applicationMetadata).length > 0) {
    serializedPolicy.applicationMetadata = JSON.stringify(applicationMetadata);
  }

  // activationSettings.activationDelay and clearanceDelay will get cleaned up in node

  serializedPolicy.activationSettings = {
    ...(serializedPolicy.activationSettings || {}),
    bumpEpochOn: APPLICATION_TO_BUMP_EPOCH_SETTINGS[getTopLevelApplication(serializedPolicy.application)]
  };

  return serializedPolicy;
}

export default {
  serialize,
  deserialize
};
